import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React from 'react';

import cs from 'app/components/Sidebar/styles.module.scss';

export interface SidebarSectionProps {
  title: React.ReactNode;
  /** Muted text after the title — “· 2 active”, “PNG · GeoTIFF · PDF”. */
  meta?: React.ReactNode;
  /**
   * Drop `meta` while the section is open, for a meta that lists what's inside
   * rather than summarizing it — once open, the body says the same thing.
   */
  metaCollapsedOnly?: boolean;
  /**
   * Controls at the trailing edge of the header row. A sibling of the toggle
   * rather than a child, so clicking them doesn't collapse the section.
   */
  rightElement?: React.ReactNode;
  /** Hide `rightElement` until the header row is hovered or focused, for a help link rather than a primary action. */
  rightElementHoverOnly?: boolean;
  /** Pass to control the section; omit to let it manage its own state. */
  isOpen?: boolean;
  defaultOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  /** Hairline above the header. Off for the first section in a panel. */
  bordered?: boolean;
  /**
   * `card` draws the section as a bordered, tinted block for nesting inside
   * another section's body.
   */
  variant?: 'plain' | 'card';
  /** Set on the outer element, for scroll targets. */
  id?: string;
  className?: string;
  bodyClassName?: string;
}

export const SidebarSection: React.FC<React.PropsWithChildren<SidebarSectionProps>> = ({
  title,
  meta,
  metaCollapsedOnly,
  rightElement,
  rightElementHoverOnly,
  isOpen: controlledIsOpen,
  defaultOpen = true,
  onToggle,
  bordered = true,
  variant = 'plain',
  id,
  className,
  bodyClassName,
  children,
}) => {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = React.useState(defaultOpen);
  const isOpen = controlledIsOpen ?? uncontrolledIsOpen;

  const handleToggle = () => {
    if (controlledIsOpen === undefined) {
      setUncontrolledIsOpen(!isOpen);
    }
    onToggle?.(!isOpen);
  };

  return (
    <div
      id={id}
      className={classnames(
        cs.section,
        {
          [cs.sectionBordered]: bordered && variant === 'plain',
          [cs.sectionCard]: variant === 'card',
        },
        className
      )}
    >
      <div className={cs.sectionHeaderRow}>
        <button
          type="button"
          className={cs.sectionHeader}
          onClick={handleToggle}
          aria-expanded={isOpen}
        >
          <span className={classnames(cs.sectionChevron, {[cs.sectionChevronClosed]: !isOpen})}>
            <B.Icon icon="chevron-down" size={16} />
          </span>
          <span className={cs.sectionTitle}>{title}</span>
          {meta && !(metaCollapsedOnly && isOpen) && (
            <span className={cs.sectionMeta}>{meta}</span>
          )}
        </button>
        {rightElement && (
          <div
            className={classnames(cs.sectionRightElement, {
              [cs.sectionRightElementHoverOnly]: rightElementHoverOnly,
            })}
          >
            {rightElement}
          </div>
        )}
      </div>
      <B.Collapse isOpen={isOpen}>
        <div className={classnames(cs.sectionBody, bodyClassName)}>{children}</div>
      </B.Collapse>
    </div>
  );
};
